"use client";

import { memo, useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useAuiState } from "@assistant-ui/react";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { apiAuthFetch, selectPraetorCase, selectPraetorDocuments } from "@/lib/authFetch";
import i18n from "@/lib/i18n";
import { fetchCaseDocuments, fetchCaseInfo, type CaseInfo, openDocumentInSidebar } from "@/lib/threadDocumentOps";
import { ClientLogger } from "@/lib/clientLogger";
import { useRouteContext } from "@/components/custom/contexts/routeContext";
import { useScopePolicy } from "@/components/custom/contexts/scopePolicyContext";
import { useThreadContext, useThreadDocsContext, type ThreadDocument } from "@/components/custom/contexts/threadContext";
import { type RemoteThreadDocument } from "@/components/custom/providers/customThreadListProvider";
import { usePendingNewThreadCaseIdStore } from "@/components/assistant-ui/pendingNewThreadStores";
import { useDocAttachStore, usePendingDocAttachStore } from "../stores";
import { CasePillRow, type CasePillRowItem } from "./CasePill";
import { ComposerDocumentsPill } from "./ComposerDocumentsPill";

// ─── Exported hook so ComposerAddMenu can trigger "Add documents" / "Add case" ────────────

export function useComposerDocActions() {
  const routeContext = useRouteContext();
  const threadState = useThreadContext();
  const threadStateRef = useRef(threadState);
  useEffect(() => { threadStateRef.current = threadState; }, [threadState]);

  const threadId = useAuiState(state => state.threadListItem.remoteId);
  const hasRemoteThread = !!threadId && !threadId.startsWith("__LOCAL");

  const { addApprovedCase } = useScopePolicy();
  const docAttachInFlight = useDocAttachStore(s => s.inFlight);
  const beginDocAttach = useDocAttachStore(s => s.begin);
  const endDocAttach = useDocAttachStore(s => s.end);
  const addPendingDocs = usePendingDocAttachStore(s => s.addPending);

  const [addError, setAddError] = useState(false);

  const handleAddDocuments = useCallback(async (caseHint?: string | null) => {
    if (docAttachInFlight) return;
    setAddError(false);
    beginDocAttach();
    try {
      const effectiveCaseHint = caseHint === undefined
        ? (routeContext.caseId ?? null)
        : caseHint;
      const picked = await selectPraetorDocuments(effectiveCaseHint);
      if (!picked.documents.length) return;

      if (hasRemoteThread) {
        const res = await apiAuthFetch(`api/threads/${threadId}/documents`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            caseId: picked.caseId,
            docs: picked.documents.map(p => ({ docId: p.id, name: p.name })),
          }),
        });
        if (!res.ok) throw new Error(i18n.t("error.code.service_unavailable"));
        const payload = await res.json() as { selectedDocs: RemoteThreadDocument[] };
        const s = threadStateRef.current;
        s.onThreadStateChanged({ ...s, selectedDocs: payload.selectedDocs });
      } else {
        const picks: RemoteThreadDocument[] = picked.documents.map(p => ({
          docId: p.id,
          name: p.name,
          caseId: picked.caseId,
        }));
        addPendingDocs(picks);
        const s = threadStateRef.current;
        const existing = s.selectedDocs ?? [];
        const seen = new Set(existing.map(d => d.docId));
        const merged = [...existing, ...picks.filter(p => !seen.has(p.docId))];
        s.onThreadStateChanged({ ...s, selectedDocs: merged });
      }
    } catch (error) {
      ClientLogger.error("Failed to add Praetor documents", {
        error: error instanceof Error ? error : undefined, component: "handleAddDocuments",
      });
      setAddError(true);
    } finally {
      endDocAttach();
    }
  }, [threadId, hasRemoteThread, docAttachInFlight, beginDocAttach, endDocAttach, addPendingDocs, routeContext.caseId]);

  // "Add case" — opens the Praetor case picker, then persists the chosen case via
  // the scope context (which routes to POST /threads/{id}/scope with addCaseIds).
  // The backend promotes the case to mainCaseId when MainCaseId is still null,
  // else appends to AdditionalCaseIds; the context mirrors that locally for the
  // composer to re-render with the new pill.
  const handleAddCase = useCallback(async () => {
    try {
      const picked = await selectPraetorCase();
      if (!picked?.id) return;
      addApprovedCase(picked.id);
    } catch (error) {
      ClientLogger.error("Failed to add Praetor case", {
        error: error instanceof Error ? error : undefined, component: "handleAddCase",
      });
    }
  }, [addApprovedCase]);

  return { handleAddDocuments, handleAddCase, addError, docAttachInFlight };
}

// ─── ComposerDocumentsStrip ──────────────────────────────────────────────────

export const ComposerDocumentsStrip = memo(function ComposerDocumentsStrip({ className }: { className?: string }) {
  const { t } = useTranslation();
  const routeContext = useRouteContext();
  const threadDocsCtx = useThreadDocsContext();
  const threadState = useThreadContext();
  const threadStateRef = useRef(threadState);
  useEffect(() => { threadStateRef.current = threadState; }, [threadState]);

  const threadId = useAuiState(state => state.threadListItem.remoteId);
  const hasRemoteThread = !!threadId && !threadId.startsWith("__LOCAL");
  const pendingDocs = usePendingDocAttachStore(s => s.pending);
  const clearPendingDocs = usePendingDocAttachStore(s => s.clearPending);

  const { threadScope, removeApprovedCase } = useScopePolicy();
  const { handleAddDocuments } = useComposerDocActions();

  const [fetchError, setFetchError] = useState(false);

  // Primary case derivation. Source-of-truth precedence:
  //   1. threadScope.mainCaseId — the persisted main anchor (set once promotion lands).
  //   2. routeContext.caseId — pre-first-dispatch, the new-thread state still
  //      carries the launch caseId via the route until the first chat dispatch
  //      promotes whatever's on the thread row.
  // Both resolve to the same case in steady state; the fallback is what lets a
  // brand-new local thread show the right primary marker before any backend write.
  const primaryCaseId = threadScope.mainCaseId ?? routeContext.caseId;

  // ── Case info fetch (primary) ─────────────────────────────────────────────
  // Phase 4 of the 2026-05-19 composer overhaul: pill is two-row (caseNumber +
  // caseName), so we now fetch the full CaseInfo, not just the name.
  const [primaryCaseInfo, setPrimaryCaseInfo] = useState<CaseInfo | null>(null);
  useEffect(() => {
    if (!primaryCaseId) { setPrimaryCaseInfo(null); return; }
    let cancelled = false;
    fetchCaseInfo(primaryCaseId).then(info => {
      if (!cancelled) setPrimaryCaseInfo(info);
    });
    return () => { cancelled = true; };
  }, [primaryCaseId]);
  const primaryCaseName = primaryCaseInfo?.name ?? null;

  // Primary pill is removable ONLY when there is no persisted mainCaseId AND no
  // launchCaseId (true local-only generic-assistant thread before any case
  // has been attached). Once promotion lands, the main case is immutable.
  const caseRemovable = !threadScope.mainCaseId && !routeContext.launchCaseId && !hasRemoteThread;
  const handleRemovePrimaryCase = useCallback(() => {
    usePendingNewThreadCaseIdStore.getState().set(null);
    routeContext.onRouteStateChanged({ caseId: undefined });
  }, [routeContext]);

  // ── Document preloading from route ────────────────────────────────────────
  const docIdsKey = routeContext.docIds?.join(",");
  useEffect(() => {
    if (!routeContext.caseId || !routeContext.docIds?.length) return;
    const requestedIds = routeContext.docIds;
    setFetchError(false);
    fetchCaseDocuments(routeContext.caseId, requestedIds)
      .catch(error => {
        ClientLogger.error("Failed to fetch documents for thread", {
          error: error instanceof Error ? error : undefined, component: "ThreadDocuments",
        });
        setFetchError(true);
        return [] as RemoteThreadDocument[];
      })
      .then(docs => {
        const resolved = (docs as RemoteThreadDocument[] | undefined) ?? [];
        const resolvedIds = new Set(resolved.map(d => d.docId));
        const placeholders: RemoteThreadDocument[] = requestedIds
          .filter(id => !resolvedIds.has(id))
          .map(id => ({ docId: id, name: "", caseId: null }));
        const merged = [...resolved, ...placeholders];
        const s = threadStateRef.current;
        s.onThreadStateChanged({ ...s, selectedDocs: merged });
      });
  }, [routeContext.caseId, docIdsKey]);

  // null selectedDocs = "not yet resolved for this case"
  useEffect(() => {
    if (routeContext.caseId === undefined) return;
    if (threadDocsCtx.selectedDocs !== null) return;
    let cancelled = false;
    fetchCaseDocuments(routeContext.caseId, [])
      .then(docs => {
        if (cancelled) return;
        const s = threadStateRef.current;
        s.onThreadStateChanged({ ...s, selectedDocs: docs });
        routeContext.onRouteStateChanged({ docIds: docs.map(d => d.docId) });
      })
      .catch((err) => {
        ClientLogger.error("Initial case doc fetch failed", {
          error: err, component: "thread/fetchCaseDocuments",
        });
        if (!cancelled) {
          const s = threadStateRef.current;
          s.onThreadStateChanged({ ...s, selectedDocs: [] });
        }
      });
    return () => { cancelled = true; };
  }, [routeContext.caseId, threadDocsCtx.selectedDocs]);

  const selectedDocs = threadDocsCtx.selectedDocs;

  // ── Last-doc-removal modal state ──────────────────────────────────────────
  // Removing the LAST doc on a case widens scope from doc-restricted to whole-
  // case wildcard. We confirm before applying so the user understands the
  // implicit broadening. Anything else (non-last doc, doc on a case that's
  // already wildcard) goes through `performRemove` directly.
  const [pendingRemoval, setPendingRemoval] = useState<{ docId: string; caseId: string; caseName: string } | null>(null);

  const performRemove = useCallback(async (docId: string) => {
    const s = threadStateRef.current;
    const previous = s.selectedDocs ?? [];
    s.onThreadStateChanged({ ...s, selectedDocs: previous.filter(d => d.docId !== docId) });

    if (!hasRemoteThread) {
      usePendingDocAttachStore.setState(state => ({
        pending: state.pending.filter(d => d.docId !== docId),
      }));
      return;
    }
    try {
      const res = await apiAuthFetch(`api/threads/${threadId}/documents/${encodeURIComponent(docId)}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error(i18n.t("error.code.service_unavailable"));
    } catch (error) {
      ClientLogger.error("Failed to remove Praetor document", {
        error: error instanceof Error ? error : undefined,
        component: "handleRemoveDocument", metadata: { docId },
      });
      const cur = threadStateRef.current;
      cur.onThreadStateChanged({ ...cur, selectedDocs: previous });
    }
  }, [threadId, hasRemoteThread]);

  const handleRemoveDocument = useCallback(async (docId: string) => {
    const cur = threadStateRef.current.selectedDocs ?? [];
    const target = cur.find(d => d.docId === docId);
    if (!target) {
      await performRemove(docId);
      return;
    }
    // Count remaining docs in the same case after this removal.
    const sameCase = cur.filter(d => d.caseId === target.caseId && d.docId !== docId);
    if (target.caseId && sameCase.length === 0) {
      // Last doc on this case — show confirmation.
      const caseName = (primaryCaseId === target.caseId ? primaryCaseName : null) ?? target.caseId;
      setPendingRemoval({ docId, caseId: target.caseId, caseName });
      return;
    }
    await performRemove(docId);
  }, [performRemove, primaryCaseId, primaryCaseName]);

  // ── Flush pending docs on thread creation ─────────────────────────────────
  useEffect(() => {
    if (!hasRemoteThread) return;
    if (pendingDocs.length === 0) return;

    let cancelled = false;
    const toFlush = pendingDocs;
    clearPendingDocs();

    (async () => {
      try {
        const byCase = new Map<string | null, RemoteThreadDocument[]>();
        for (const d of toFlush) {
          const cid = d.caseId ?? null;
          const list = byCase.get(cid) ?? [];
          list.push(d);
          byCase.set(cid, list);
        }

        let latestSelectedDocs: RemoteThreadDocument[] | null = null;
        for (const [caseId, docs] of byCase) {
          const res = await apiAuthFetch(`api/threads/${threadId}/documents`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              caseId,
              docs: docs.map(d => ({ docId: d.docId, name: d.name })),
            }),
          });
          if (!res.ok) throw new Error(i18n.t("error.code.service_unavailable"));
          const payload = await res.json() as { selectedDocs: RemoteThreadDocument[] };
          latestSelectedDocs = payload.selectedDocs;
        }
        if (!cancelled && latestSelectedDocs) {
          const s = threadStateRef.current;
          s.onThreadStateChanged({ ...s, selectedDocs: latestSelectedDocs });
        }
      } catch (error) {
        ClientLogger.error("Failed to flush pending Praetor documents", {
          error: error instanceof Error ? error : undefined, component: "flushPendingDocs",
        });
      }
    })();

    return () => { cancelled = true; };
  }, [hasRemoteThread, threadId, pendingDocs, clearPendingDocs]);

  // ── Pill partitioning ─────────────────────────────────────────────────────
  // primaryCaseDocs: docs whose caseId matches primaryCaseId (rendered under the primary pill).
  // additionalByCaseId: docs grouped by their other caseId.
  // additionalCaseIdsWithNoDocs: cases the user attached but haven't selected any docs for —
  //   render as wildcard-shape pills (count badge hidden).
  // unknownCaseDocs: docs with no caseId.
  const additionalCaseIdsFromScope = threadScope.additionalCaseIds ?? [];

  const { primaryCaseDocs, additionalByCaseId, unknownCaseDocs } = useMemo(() => {
    const primary: ThreadDocument[] = [];
    const byCase = new Map<string, ThreadDocument[]>();
    const unknown: ThreadDocument[] = [];
    for (const d of selectedDocs ?? []) {
      const cid = d.caseId ?? null;
      if (cid === null) { unknown.push(d); continue; }
      if (primaryCaseId !== undefined && cid === primaryCaseId) { primary.push(d); continue; }
      const list = byCase.get(cid) ?? [];
      list.push(d);
      byCase.set(cid, list);
    }
    // Seed empty buckets for additional cases that have no docs yet — they
    // should still render as wildcard pills.
    for (const id of additionalCaseIdsFromScope) {
      if (id === primaryCaseId) continue;
      if (!byCase.has(id)) byCase.set(id, []);
    }
    return { primaryCaseDocs: primary, additionalByCaseId: byCase, unknownCaseDocs: unknown };
  }, [selectedDocs, primaryCaseId, additionalCaseIdsFromScope]);

  // Case-info fetch for additional pills (Phase 4: two-row needs caseNumber too).
  const additionalCaseIdsKey = useMemo(
    () => Array.from(additionalByCaseId.keys()).sort().join(","),
    [additionalByCaseId],
  );
  const [caseInfos, setCaseInfos] = useState<Record<string, CaseInfo>>({});
  useEffect(() => {
    let cancelled = false;
    const ids = Array.from(additionalByCaseId.keys()).filter(id => !(id in caseInfos));
    if (ids.length === 0) return;
    Promise.all(ids.map(async id => [id, await fetchCaseInfo(id)] as const))
      .then(results => {
        if (cancelled) return;
        setCaseInfos(prev => {
          const next = { ...prev };
          for (const [id, info] of results) {
            if (info) next[id] = info;
          }
          return next;
        });
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalCaseIdsKey]);

  const sortedAdditionalEntries = useMemo(
    () => Array.from(additionalByCaseId.entries()).sort(([a], [b]) => {
      const an = caseInfos[a]?.name ?? a;
      const bn = caseInfos[b]?.name ?? b;
      return an.localeCompare(bn);
    }),
    [additionalByCaseId, caseInfos],
  );

  // ── Visibility check ──────────────────────────────────────────────────────
  const hasDocs = selectedDocs && selectedDocs.length > 0;
  const hasPrimaryCase = !!primaryCaseId;
  const hasAdditionalCases = additionalCaseIdsFromScope.length > 0;
  const isLoading = selectedDocs === null && hasPrimaryCase;
  const visible = hasPrimaryCase || hasDocs || hasAdditionalCases;

  if (!visible && !isLoading) return null;

  // ── Pill assembly (one flat list so CasePillRow can measure them together) ─
  const openDoc = (docId: string) =>
    openDocumentInSidebar(docId, undefined, routeContext.threadId);

  const pillItems: CasePillRowItem[] = [];
  if (hasPrimaryCase && primaryCaseId) {
    pillItems.push({
      key: `primary:${primaryCaseId}`,
      caseNumber: primaryCaseInfo?.caseNumber ?? undefined,
      name: primaryCaseName ?? t("composer.caseChip.label"),
      docs: primaryCaseDocs,
      primary: true,
      onRemoveDoc: handleRemoveDocument,
      onOpenDoc: openDoc,
      onRemoveSpis: caseRemovable ? handleRemovePrimaryCase : undefined,
      onAddDocs: () => handleAddDocuments(primaryCaseId),
    });
  }
  for (const [cid, docs] of sortedAdditionalEntries) {
    pillItems.push({
      key: `add:${cid}`,
      caseNumber: caseInfos[cid]?.caseNumber ?? undefined,
      name: caseInfos[cid]?.name ?? t("composer.caseChip.label"),
      docs,
      onRemoveDoc: handleRemoveDocument,
      onOpenDoc: openDoc,
      onRemoveSpis: () => removeApprovedCase(cid),
      onAddDocs: () => handleAddDocuments(cid),
    });
  }
  if (unknownCaseDocs.length > 0) {
    pillItems.push({
      key: "loose",
      name: t("documents.looseDocuments"),
      docs: unknownCaseDocs,
      onRemoveDoc: handleRemoveDocument,
      onOpenDoc: openDoc,
    });
  }

  // ── Render ────────────────────────────────────────────────────────────────
  // Phase 4: strip switches from `flex-wrap` (pills line-wrap when too many)
  // to single-row no-wrap. Equal-width compaction (inside CasePillRow) takes
  // over once natural pill widths overflow the available row width — the
  // user sees roughly the same prefix of each pill rather than some pills
  // bumped to a second line. Strip auto-grows to ~48px to fit two-row pills.
  return (
    <>
      <div className={cn("aui-documents-strip flex items-center min-h-12 border-b border-border/50 bg-muted/30 overflow-hidden px-3 py-1.5 gap-1.5", className)}>
        {isLoading && <Spinner />}
        <CasePillRow items={pillItems} />
        <div className="ml-auto shrink-0">
          <ComposerDocumentsPill />
        </div>
        {fetchError && (
          <span className="text-xs text-destructive shrink-0">{t("documents.loadFailed")}</span>
        )}
      </div>

      <LastDocRemovalDialog
        state={pendingRemoval}
        onConfirm={() => {
          if (!pendingRemoval) return;
          const docId = pendingRemoval.docId;
          setPendingRemoval(null);
          void performRemove(docId);
        }}
        onCancel={() => setPendingRemoval(null)}
      />
    </>
  );
});

// ─── Last-doc-removal confirmation dialog ────────────────────────────────────

const LastDocRemovalDialog: FC<{
  state: { docId: string; caseId: string; caseName: string } | null;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ state, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  if (!state) return null;
  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{t("composer.lastDocRemoval.title")}</DialogTitle>
          <DialogDescription>
            {t("composer.lastDocRemoval.message", { name: state.caseName })}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>
            {t("composer.lastDocRemoval.cancel")}
          </Button>
          <Button variant="default" onClick={onConfirm}>
            {t("composer.lastDocRemoval.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
